// src/pages/public/CataloguePage.jsx
import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { productsApi } from '@/api/services'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { useCart } from '@/context/CartContext'
import { PageSpinner, Pagination, Badge } from '@/components/common/ui'
import { Search, Filter, ShoppingCart, Star, SlidersHorizontal, X } from 'lucide-react'
import toast from 'react-hot-toast'

const SORT_OPTIONS = [
  { value: 'newest',     label: 'Newest First' },
  { value: 'price_asc',  label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'popular',    label: 'Most Popular' },
  { value: 'rating',     label: 'Best Rated' },
]

function ProductCard({ product }) {
  const { format } = useCurrency()
  const { addItem } = useCart()

  return (
    <div className="group bg-white border border-border rounded-2xl overflow-hidden hover:shadow-card-md transition-all">
      <Link to={`/fabrics/${product.slug}`} className="block aspect-[4/3] bg-surface overflow-hidden relative">
        {product.primary_image
          ? <img src={product.primary_image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          : <div className="w-full h-full flex items-center justify-center text-5xl">🧵</div>
        }
        {product.is_featured && <span className="absolute top-2 left-2 bg-gradient-brand text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Featured</span>}
      </Link>
      <div className="p-4">
        <p className="text-xs text-muted mb-0.5">{product.category?.name}</p>
        <Link to={`/fabrics/${product.slug}`} className="font-bold text-sm hover:text-pink transition-colors line-clamp-2 leading-snug">
          {product.name}
        </Link>
        <p className="text-xs text-muted mt-1 truncate">{product.supplier?.business_name}</p>
        <div className="flex items-center gap-1 mt-2">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={10} className={i < Math.floor(product.average_rating || 0) ? 'text-orange-400 fill-orange-400' : 'text-gray-200 fill-gray-200'} />
          ))}
          <span className="text-xs text-muted ml-0.5">({product.reviews_count || 0})</span>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div>
            <span className="font-extrabold text-gray-900">{format(product.price)}</span>
            <span className="text-xs text-muted">/{product.unit || 'm'}</span>
          </div>
          <button onClick={() => addItem(product.id, product.moq || 1).then(() => toast.success('Added!')).catch(() => toast.error('Login to add'))}
            className="w-8 h-8 rounded-full bg-gradient-brand text-white flex items-center justify-center hover:shadow-brand-sm transition-all">
            <ShoppingCart size={13} />
          </button>
        </div>
        {product.moq > 1 && <p className="text-[11px] text-muted mt-1">MOQ: {product.moq}{product.unit || 'm'}</p>}
        {product.is_gi_tagged && (
          <span className="inline-block mt-1.5 text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
            🏷️ GI Tagged{product.gi_tag_name ? ` — ${product.gi_tag_name}` : ''}
          </span>
        )}
      </div>
    </div>
  )
}

export default function CataloguePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showFilters, setShowFilters]   = useState(false)

  const params = {
    q:        searchParams.get('q') || '',
    category: searchParams.get('category') || '',
    sort:     searchParams.get('sort') || 'newest',
    page:     searchParams.get('page') || 1,
    min_price:searchParams.get('min_price') || '',
    max_price:searchParams.get('max_price') || '',
  }

  const { data, isLoading } = useQuery({
    queryKey: ['products', params],
    queryFn:  () => productsApi.list(params).then(r => r.data),
    keepPreviousData: true,
  })

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn:  () => productsApi.categories().then(r => r.data.data),
  })

  const updateParam = (key, val) => {
    const next = new URLSearchParams(searchParams)
    val ? next.set(key, val) : next.delete(key)
    next.delete('page')
    setSearchParams(next)
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row gap-6">

        {/* ── Filters sidebar ────────────────────────────────────────── */}
        <aside className={`md:w-56 flex-shrink-0 ${showFilters ? 'block' : 'hidden md:block'}`}>
          <div className="bg-white border border-border rounded-2xl p-5 sticky top-20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Filters</h3>
              <button onClick={() => setShowFilters(false)} className="md:hidden text-muted"><X size={16}/></button>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-wide text-muted mb-3">Category</p>
              <div className="space-y-1">
                <button onClick={() => updateParam('category', '')}
                  className={`w-full text-left text-sm px-2 py-1.5 rounded-lg font-medium ${!params.category ? 'text-pink bg-pink-light' : 'text-muted hover:text-gray-900 hover:bg-surface'}`}>
                  All Fabrics
                </button>
                {categories?.map(cat => (
                  <button key={cat.id} onClick={() => updateParam('category', cat.slug)}
                    className={`w-full text-left text-sm px-2 py-1.5 rounded-lg font-medium ${params.category === cat.slug ? 'text-pink bg-pink-light' : 'text-muted hover:text-gray-900 hover:bg-surface'}`}>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Price range */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-muted mb-3">Price / metre</p>
              <div className="flex gap-2">
                <input type="number" placeholder="Min" value={params.min_price}
                  onChange={e => updateParam('min_price', e.target.value)}
                  className="input w-full py-1.5 text-xs" />
                <input type="number" placeholder="Max" value={params.max_price}
                  onChange={e => updateParam('max_price', e.target.value)}
                  className="input w-full py-1.5 text-xs" />
              </div>
            </div>
          </div>
        </aside>

        {/* ── Main grid ──────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="relative flex-1 min-w-48">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input defaultValue={params.q} placeholder="Search fabrics…"
                onKeyDown={e => e.key === 'Enter' && updateParam('q', e.target.value)}
                className="input pl-9 h-10 rounded-full" />
            </div>
            <select value={params.sort} onChange={e => updateParam('sort', e.target.value)}
              className="input h-10 w-auto rounded-full text-sm">
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button onClick={() => setShowFilters(v => !v)}
              className="md:hidden btn-secondary h-10 px-4 text-sm">
              <SlidersHorizontal size={15} /> Filters
            </button>
          </div>

          {/* Active filters */}
          {(params.category || params.q) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {params.q && <Badge variant="pink" className="gap-1.5">{params.q} <button onClick={() => updateParam('q','')}>×</button></Badge>}
              {params.category && <Badge variant="blue" className="gap-1.5">{params.category} <button onClick={() => updateParam('category','')}>×</button></Badge>}
            </div>
          )}

          {isLoading ? <PageSpinner /> : (
            <>
              <p className="text-xs text-muted mb-4">{data?.meta?.total || 0} products</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {data?.data?.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
              <Pagination meta={data?.meta} onPageChange={p => updateParam('page', p)} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
